import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalBody, Spinner } from 'reactstrap';
import { debounce } from 'lodash';
import { toast } from 'react-toastify';

import locationApi from 'api/locationApi';
import { ReactComponent as CloseButtonSvg } from 'assets/images/location/close-button-svg.svg';

import styles from './ModalAddFavoritePlace.module.scss';

function ModalAddFavoritePlace(props) {
  const { isOpen, onClose, onSelectPlace } = props;

  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchVal, setSearchVal] = useState('');

  const fetchPlaces = useCallback(async (q) => {
    setIsLoading(true);
    try {
      const response = await locationApi.getIntranetLocationList({
        q,
        isFavoriteOrHot: false,
        limit: 10,
      });
      const data = response?.payload?.data || [];
      // Client-side filter to ensure we only show places that aren't already favorites
      const nonFavoritePlaces = data.filter((item) => !item.isFavorite);
      setLocations(nonFavoritePlaces);
    } catch (error) {
      toast.error('Không thể lấy danh sách chỗ nghỉ');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const debouncedFetch = useMemo(
    () => debounce((q) => fetchPlaces(q), 400),
    [fetchPlaces],
  );

  useEffect(() => {
    if (isOpen) {
      fetchPlaces('');
      setSearchVal('');
    }
  }, [isOpen, fetchPlaces]);

  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchVal(value);
    debouncedFetch(value);
  };

  const CloseButton = useMemo(
    () => (
      <button className={styles.ButtonClose} onClick={onClose} type="button">
        <CloseButtonSvg />
      </button>
    ),
    [onClose],
  );

  const renderListContent = () => {
    if (isLoading) {
      return (
        <div className={styles.LoadingWrapper}>
          <Spinner color="primary" />
        </div>
      );
    }

    if (locations.length === 0) {
      return (
        <div className={styles.EmptyMessage}>
          Không tìm thấy chỗ nghỉ phù hợp
        </div>
      );
    }

    return locations.map((item) => (
      <div key={item._id} className={styles.PlaceItem}>
        <div className={styles.PlaceInfo}>
          <span className={styles.PlaceId}>
            {item.placeId || item._id}
          </span>
          <span className={styles.PlaceName}>
            {item.name}
          </span>
          <div className={styles.PlaceMeta}>
            <span>{item.market?.name || 'Chưa chọn thị trường'}</span>
            {item.province?.name && (
              <>
                <div className={styles.Divider} />
                <span>{item.province.name}</span>
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          className={styles.SelectBtn}
          onClick={() => onSelectPlace(item._id)}
        >
          Chọn
        </button>
      </div>
    ));
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="md"
      centered
      fade
    >
      <div className={styles.ModalHeader}>
        <span className={styles.ModalTitle}>Thêm chỗ nghỉ yêu thích</span>
        {CloseButton}
      </div>
      <ModalBody className={styles.ModalBody}>
        <div className={styles.SearchContainer}>
          <input
            type="text"
            placeholder="Tìm theo ID, Tên chỗ nghỉ..."
            value={searchVal}
            onChange={handleSearchChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
            className={styles.SearchInput}
          />
          <i className={`fas fa-search ${styles.SearchIcon}`} />
        </div>

        <div className={styles.ListContainer}>
          {renderListContent()}
        </div>
      </ModalBody>
    </Modal>
  );
}

ModalAddFavoritePlace.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectPlace: PropTypes.func.isRequired,
};

export default React.memo(ModalAddFavoritePlace);
